// phase-compiler-six-tabs — the ONE canonical Review surface.
//
// The six-main-tab consolidation collapses the two former document viewers —
// the centre pane's `CompilerPreviewPanel` (PDF + DOCX) and the Inspector's
// `PreviewTab` (PDF + XML/SPL + XFA + PDF/A compliance + fullscreen) — into a
// single Review tab, with Compare and Annotations as CONTEXTUAL views (a
// compact mode selector, never a nested tab row). Both legacy components keep
// their public API and stay as wrappers for any other consumer; nothing here
// edits them.
//
// WHY a canvas, not an iframe: the Codex in-app browser has no built-in PDF
// viewer, so an `<iframe src=blob:…>` renders BLANK in the actual runtime. The
// navigation workspace already solved this with `PdfCanvasViewer` (pdf.js →
// real pixels on a <canvas>, bundled worker, scripting disabled). Review reuses
// it for both the single-document preview AND the Compare panes so the operator
// sees real pixels everywhere. DOCX still renders as sandboxed mammoth HTML via
// `useDocxHtmlPreview`; XML/SPL keep their syntax-highlighted / SPL-preview
// controls; XFA forms keep the honest "download to Adobe" fallback.
//
// The single selected-file identity header (filename + lifecycle/signature
// badges) is owned by the workspace shell and rendered once above every tab, so
// this surface deliberately does NOT repeat a filename toolbar — it shows only
// the placed-section context, the document ◀▶ flip, the mode selector and a
// fullscreen toggle.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { v2, type Document, type SplMetadata } from '@/api/v2Client';
import { useInlineDocumentView } from '@/hooks/useInlineDocumentView';
import { useDocxHtmlPreview } from '@/hooks/useDocxHtmlPreview';
import { highlightXml } from '@/utils/highlightXml';
import { PreviewNavCluster } from '@/components/shared/PreviewNavCluster';
import { PdfComplianceSummary } from './inspector/PdfComplianceSummary';
import { PdfCanvasViewer } from './inspector/navigation/PdfCanvasViewer';
import { AnnotationPanel } from '@/components/AnnotationPanel';

export interface WorkspaceReviewPanelProps {
  /** The selected document, or null when nothing is selected. */
  readonly document: Document | null;
  readonly submissionId: string;
  /** Candidate documents for Compare — typically the whole submission list. */
  readonly siblings: ReadonlyArray<{ readonly id: string; readonly fileName: string }>;
  /**
   * The shared viewer page, owned by CompilerPage. It resets to 1 on a document
   * change there; this surface reflects it into the canvas and reports user
   * page moves back out via {@link onPageChange}.
   */
  readonly page: number;
  readonly onPageChange: (page: number) => void;
  readonly hasPrevDocument: boolean;
  readonly hasNextDocument: boolean;
  readonly onPrevDocument: () => void;
  readonly onNextDocument: () => void;
}

type ReviewMode = 'preview' | 'compare' | 'annotations';

/** Compare renders both panes through pdf.js, so a candidate must be a PDF.
 *  Case-insensitive extension check — the same `.pdf` test that gates the
 *  single-document preview (`isPdf`), applied to a sibling's fileName. */
function isPdfFileName(fileName: string): boolean {
  return fileName.toLowerCase().endsWith('.pdf');
}

// ── authenticated byte helpers ──────────────────────────────────────────────
//
// phase-compiler-six-tabs correction — these go through `v2.documents.*`, which
// is the MSAL-authenticated `apiFetch` path, NOT a raw `fetch(/api/v1/...)`.
// A raw fetch only "worked" locally because dev runs with AUTH_ENABLED=false;
// in production it carries no bearer token and the byte reads/download would
// fail. Reusing the authenticated client is the single supported way to read a
// document's bytes.

const XFA_SCAN_BYTES = 32_768; // 32 KB — enough to find /XFA in the AcroForm dict

/** Tri-state so an inspection FAILURE is never silently reported as "not XFA".
 *  A failed read is `'unknown'`, which the surface renders honestly rather than
 *  falling through to the canvas as if the form were previewable. */
type XfaVerdict = 'yes' | 'no' | 'unknown';

async function detectXfa(documentId: string): Promise<XfaVerdict> {
  try {
    const res = await v2.documents.viewFile(documentId);
    if (!res.ok) return 'unknown';
    const buf = await res.arrayBuffer();
    const head = buf.byteLength > XFA_SCAN_BYTES ? buf.slice(0, XFA_SCAN_BYTES) : buf;
    const text = new globalThis.TextDecoder('ascii', { fatal: false }).decode(head);
    return text.includes('/XFA') || text.includes('xdp:xdp') ? 'yes' : 'no';
  } catch {
    return 'unknown';
  }
}

async function fetchXmlContent(documentId: string): Promise<string> {
  const res = await v2.documents.viewFile(documentId);
  if (!res.ok) throw new Error(`Could not load XML file (${res.status})`);
  return res.text();
}

/** Authenticated download: fetch the bytes through `apiFetch`, then save via a
 *  transient object-URL anchor. A bare `<a href=/api/v1/...>` would open an
 *  UNauthenticated request in production. */
async function downloadDocument(documentId: string, fileName: string | null): Promise<void> {
  const res = await v2.documents.downloadFile(documentId);
  if (!res.ok) throw new Error(`Could not download the file (${res.status}).`);
  const blob = await res.blob();
  const objectUrl = globalThis.URL.createObjectURL(blob);
  try {
    const a = globalThis.document.createElement('a');
    a.href = objectUrl;
    a.download = fileName ?? 'document';
    globalThis.document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    globalThis.URL.revokeObjectURL(objectUrl);
  }
}

// ── styles ──────────────────────────────────────────────────────────────────

const rootStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: 1,
  fontSize: 12,
};

const fullscreenStyle: CSSProperties = {
  height: '100%',
  background: 'var(--surface, #fff)',
  padding: 12,
  boxSizing: 'border-box',
};

const toolbarRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  marginBottom: 8,
};

const modeGroupStyle: CSSProperties = {
  display: 'inline-flex',
  gap: 2,
  border: '1px solid var(--border, #E5E7EB)',
  borderRadius: 6,
  padding: 2,
};

const modeBtnStyle = (active: boolean): CSSProperties => ({
  padding: '4px 12px',
  fontSize: 12,
  fontWeight: active ? 700 : 500,
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  background: active ? 'var(--accent, #4f46e5)' : 'transparent',
  color: active ? '#fff' : 'var(--text-muted, #6B7280)',
});

const toolbarBtnStyle: CSSProperties = {
  padding: '4px 10px',
  fontSize: 12,
  background: 'transparent',
  border: '1px solid var(--border, #E5E7EB)',
  borderRadius: 4,
  cursor: 'pointer',
  color: 'var(--text-muted, #6B7280)',
};

const viewerAreaStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

const stateStyle: CSSProperties = {
  padding: '20px 12px',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
  textAlign: 'center',
};

const compareLayout: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 8,
  minHeight: 320,
};

const compareLabel: CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-muted)',
  fontWeight: 700,
  marginBottom: 4,
};

const compareSelect: CSSProperties = {
  padding: '4px 8px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  fontSize: 13,
  background: 'var(--surface)',
  color: 'var(--text)',
  cursor: 'pointer',
  marginBottom: 6,
  maxWidth: '100%',
};

const splParseBtn: CSSProperties = { ...toolbarBtnStyle };

const xfaDownloadBtn: CSSProperties = {
  padding: '8px 20px',
  background: 'var(--primary, #1B3A6B)',
  color: 'white',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

// The PDF/A compliance card is reference clutter, not the reading surface: fully
// expanded it displaced the PDF below the fold (browser review, 2026-10-28).
// It lives in a CLOSED-by-default native <details> so the canvas is immediately
// visible; `flex: 0 0 auto` keeps the disclosure from stealing the viewer's
// height whether collapsed or open.
const complianceDisclosureStyle: CSSProperties = {
  flex: '0 0 auto',
};

const complianceSummaryStyle: CSSProperties = {
  cursor: 'pointer',
  padding: '6px 2px',
  marginBottom: 4,
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-muted, #6B7280)',
};

export function WorkspaceReviewPanel({
  document,
  submissionId,
  siblings,
  page,
  onPageChange,
  hasPrevDocument,
  hasNextDocument,
  onPrevDocument,
  onNextDocument,
}: WorkspaceReviewPanelProps): ReactElement {
  const documentId = document?.Id ?? null;
  const fileName = document?.FileName ?? null;
  const hasFile = Boolean(document?.Sha256Hash ?? document?.FileUploadedAt);
  const lower = (fileName ?? '').toLowerCase();
  const isPdf = lower.endsWith('.pdf');
  const isDocx = !isPdf && lower.endsWith('.docx');
  const isXml = lower.endsWith('.xml');
  const placedSectionCode = document?.CtdSectionCode ?? null;
  const isConfirmed = (document?.ConfirmedAt ?? null) !== null;
  const defaultCompareId = document?.ReplacesDocumentId ?? null;

  const [mode, setMode] = useState<ReviewMode>('preview');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // XFA + XML/SPL state. Every async-derived value is TAGGED with the document
  // id it was fetched for, and rendering reads it only when the tag still
  // matches the selected document. That is a SYNCHRONOUS identity gate: the
  // instant the selection changes, last document's content stops rendering —
  // before the new effect has even run — so a late-arriving response for a
  // prior document can never paint into the new one (and the in-flight
  // `cancelled` guards stop it writing state at all).
  const [xfa, setXfa] = useState<{ docId: string; verdict: 'loading' | XfaVerdict }>({
    docId: '',
    verdict: 'no',
  });
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [xml, setXml] = useState<{
    docId: string;
    loading: boolean;
    error: string | null;
    content: string | null;
    isSpl: boolean;
    splHtml: string | null;
    splHtmlLoading: boolean;
  } | null>(null);
  const [splParse, setSplParse] = useState<{
    docId: string;
    loading: boolean;
    error: string | null;
    data: SplMetadata | null;
  }>({ docId: '', loading: false, error: null, data: null });

  // Compare selection. Both panes feed pdf.js, so a DOCX/XML sibling can never
  // be a target — offering one would parse non-PDF bytes as a PDF (Kimi F2).
  // Filter to PDF siblings across the WHOLE candidate list (all-submission
  // candidates are preserved; only non-PDFs are dropped).
  const compareOptions = useMemo(
    () => siblings.filter((s) => s.id !== documentId && isPdfFileName(s.fileName)),
    [siblings, documentId],
  );
  const [compareId, setCompareId] = useState<string | null>(null);
  // Synchronous eligibility gate: never load a target that is not a current
  // option. When the candidate set changes, `compareId` can briefly hold a
  // stale/ineligible id before the reset effect runs — this keeps those bytes
  // out of the renderer on that same render.
  const eligibleCompareId = useMemo(
    () => (compareOptions.some((o) => o.id === compareId) ? compareId : null),
    [compareOptions, compareId],
  );

  // Inline-view hooks. All hooks run on every render (rules of hooks); the
  // `enabled` flag + mode gating keeps them from fetching off-screen.
  const previewPdf = useInlineDocumentView(documentId, {
    enabled: hasFile && isPdf && mode === 'preview',
  });
  const docx = useDocxHtmlPreview(documentId, {
    enabled: hasFile && isDocx && mode === 'preview',
  });
  const compareCurrent = useInlineDocumentView(documentId, {
    enabled: isPdf && mode === 'compare',
  });
  const compareTarget = useInlineDocumentView(eligibleCompareId, {
    enabled: isPdf && mode === 'compare',
  });

  // The focus the canvas resolves to. Driving it from the SHARED page means a
  // document change (CompilerPage resets page → 1) re-centres the canvas, while
  // a user page move inside the canvas reports back via onPageChange and simply
  // re-applies the same page here (no feedback loop). No highlight → nothing is
  // painted over the page.
  const pdfFocus = useMemo(() => ({ page }), [page]);

  // Reset the contextual mode and re-pick the compare target when the selected
  // document changes. Compare is unavailable off a non-PDF, so fall back to
  // preview if the user had Compare open.
  useEffect(() => {
    setMode((m) => (m === 'compare' && !isPdf ? 'preview' : m));
  }, [documentId, isPdf]);

  useEffect(() => {
    if (defaultCompareId && compareOptions.some((o) => o.id === defaultCompareId)) {
      setCompareId(defaultCompareId);
      return;
    }
    setCompareId(compareOptions[0]?.id ?? null);
  }, [documentId, defaultCompareId, compareOptions]);

  // XFA detection (PDF preview only). Tagged with the requesting document id; a
  // failed inspection records 'unknown', never a silent 'no'.
  useEffect(() => {
    if (!documentId || !isPdf || !hasFile || mode !== 'preview') {
      setXfa({ docId: documentId ?? '', verdict: 'no' });
      return;
    }
    const reqId = documentId;
    let cancelled = false;
    setXfa({ docId: reqId, verdict: 'loading' });
    setDownloadError(null);
    void detectXfa(reqId).then((verdict) => {
      if (cancelled) return;
      setXfa({ docId: reqId, verdict });
    });
    return () => {
      cancelled = true;
    };
  }, [documentId, isPdf, hasFile, mode]);

  // XML content fetch + SPL detection/preview (XML preview only). Every result
  // is tagged with `reqId` and every completion path checks `cancelled` AND the
  // tag, so a prior document's XML/SPL response can never overwrite the current
  // selection's state. `v2.documents.viewFile` carries no AbortSignal, so the
  // `cancelled` flag (set in cleanup, synchronously, on a selection change) is
  // what suppresses a superseded in-flight read.
  useEffect(() => {
    if (!documentId || !isXml || mode !== 'preview') return;
    const reqId = documentId;
    let cancelled = false;
    setXml({
      docId: reqId,
      loading: true,
      error: null,
      content: null,
      isSpl: false,
      splHtml: null,
      splHtmlLoading: false,
    });
    fetchXmlContent(reqId)
      .then((text) => {
        if (cancelled) return;
        const splDetected = text.includes('urn:hl7-org:v3');
        setXml({
          docId: reqId,
          loading: false,
          error: null,
          content: text,
          isSpl: splDetected,
          splHtml: null,
          splHtmlLoading: splDetected,
        });
        if (splDetected) {
          v2.spl
            .preview(reqId)
            .then((result) => {
              if (cancelled) return;
              setXml((prev) =>
                prev && prev.docId === reqId
                  ? { ...prev, splHtml: result.html, splHtmlLoading: false }
                  : prev,
              );
            })
            .catch(() => {
              if (cancelled) return;
              setXml((prev) =>
                prev && prev.docId === reqId
                  ? { ...prev, splHtml: null, splHtmlLoading: false }
                  : prev,
              );
            });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setXml({
          docId: reqId,
          loading: false,
          error: err instanceof Error ? err.message : 'Could not load XML',
          content: null,
          isSpl: false,
          splHtml: null,
          splHtmlLoading: false,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [documentId, isXml, mode]);

  // Fullscreen toggle + browser-driven (Esc) state tracking.
  const toggleFullscreen = (): void => {
    if (!containerRef.current) return;
    if (!globalThis.document.fullscreenElement) {
      void containerRef.current.requestFullscreen();
    } else {
      void globalThis.document.exitFullscreen();
    }
  };
  useEffect(() => {
    const onFsChange = (): void => {
      setIsFullscreen(globalThis.document.fullscreenElement !== null);
    };
    globalThis.document.addEventListener('fullscreenchange', onFsChange);
    return () => globalThis.document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const handleDownload = (): void => {
    if (documentId === null) return;
    setDownloadError(null);
    void downloadDocument(documentId, fileName).catch((err: unknown) => {
      setDownloadError(err instanceof Error ? err.message : 'Could not download the file.');
    });
  };

  // ── honest empty state ──────────────────────────────────────────────────
  if (!document || documentId === null) {
    return (
      <div data-testid="review-empty" style={{ ...stateStyle, padding: 24 }}>
        Select a document from the CTD tree to review it here. On a small screen, open the CTD
        tree above.
      </div>
    );
  }

  const sectionContextHeader = placedSectionCode ? (
    <div
      data-testid="review-section-context"
      style={{
        padding: '8px 10px',
        marginBottom: 8,
        borderRadius: 4,
        background: isConfirmed ? 'rgba(34,197,94,0.08)' : 'rgba(245,158,11,0.08)',
        border: `1px solid ${isConfirmed ? 'rgba(34,197,94,0.35)' : 'rgba(245,158,11,0.40)'}`,
        fontSize: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: '#6B7280' }}>Placed in section:</span>
        <strong style={{ color: '#111827' }}>{placedSectionCode}</strong>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: isConfirmed ? '#15803D' : '#92400E',
            marginLeft: 'auto',
          }}
        >
          {isConfirmed ? '✓ CONFIRMED' : '⚠ PENDING REVIEW'}
        </span>
      </div>
    </div>
  ) : null;

  function renderPreview(): ReactElement {
    if (!hasFile) {
      return (
        <div data-testid="review-no-file" style={stateStyle}>
          No file uploaded yet — upload a file to review it here.
        </div>
      );
    }
    if (isPdf) {
      // Synchronous identity gate — a verdict carried over from a prior
      // selection reads as 'loading' until this document's own scan resolves.
      const xfaVerdict: 'loading' | XfaVerdict = xfa.docId === documentId ? xfa.verdict : 'loading';
      return (
        <div style={viewerAreaStyle}>
          {/* Closed-by-default so the PDF stays above the fold — the fully
              expanded compliance card consumed all available height and pushed
              the canvas below the viewport (browser review 2026-10-28). The
              full card (with its own pass/fail pill) lives inside the open
              disclosure; <details> keeps it in the DOM when collapsed. */}
          <details data-testid="review-compliance-disclosure" style={complianceDisclosureStyle}>
            <summary data-testid="review-compliance-summary" style={complianceSummaryStyle}>
              PDF/A compliance checks
            </summary>
            <div style={{ marginTop: 8 }}>
              <PdfComplianceSummary documentId={documentId as string} />
            </div>
          </details>
          {downloadError && (
            <div
              data-testid="review-download-error"
              role="alert"
              style={{
                padding: 8,
                margin: '0 auto 8px',
                maxWidth: 400,
                background: '#FEE2E2',
                color: '#B91C1C',
                border: '1px solid #B91C1C',
                borderRadius: 4,
                fontSize: 12,
                textAlign: 'center',
              }}
            >
              {downloadError}
            </div>
          )}
          {xfaVerdict === 'yes' ? (
            <div
              data-testid="review-xfa-fallback"
              style={{ padding: 32, textAlign: 'center', maxWidth: 400, margin: '0 auto' }}
            >
              <div style={{ fontSize: 28, marginBottom: 12 }}>🗃️</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text, #111827)', marginBottom: 8 }}>
                XFA Form — Adobe Reader Required
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted, #6B7280)', lineHeight: 1.6, marginBottom: 16 }}>
                This is an XFA (XML Forms Architecture) form that requires Adobe Acrobat or Adobe
                Reader to view and fill. XFA forms cannot be previewed in the browser.
              </div>
              <button
                type="button"
                data-testid="review-xfa-download"
                onClick={handleDownload}
                style={xfaDownloadBtn}
              >
                Download to open in Adobe
              </button>
            </div>
          ) : xfaVerdict === 'unknown' ? (
            // Honest UNKNOWN — the inspection read failed, so we do NOT claim
            // this is a normal, previewable PDF. We offer the authenticated
            // download rather than rendering the canvas on an unverified file.
            <div
              data-testid="review-xfa-unknown"
              role="alert"
              style={{ padding: 32, textAlign: 'center', maxWidth: 420, margin: '0 auto' }}
            >
              <div style={{ fontSize: 28, marginBottom: 12 }}>⚠️</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text, #111827)', marginBottom: 8 }}>
                Could not verify this PDF
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted, #6B7280)', lineHeight: 1.6, marginBottom: 16 }}>
                The file could not be read to check whether it is an XFA form, so it is not being
                previewed here. Download it to open in a desktop reader.
              </div>
              <button
                type="button"
                data-testid="review-xfa-download"
                onClick={handleDownload}
                style={xfaDownloadBtn}
              >
                Download file
              </button>
            </div>
          ) : xfaVerdict === 'loading' ? (
            <div data-testid="review-loading" style={stateStyle}>
              Loading preview…
            </div>
          ) : (
            // Real pixels via pdf.js — a native iframe renders blank in the
            // in-app browser. The canvas owns its page controls; the shared
            // page drives it through `focus` and reports back via onPageChange.
            <PdfCanvasViewer
              idPrefix="review-pdf"
              url={previewPdf.url}
              loading={previewPdf.loading}
              error={previewPdf.error}
              focus={pdfFocus}
              onPageChange={onPageChange}
            />
          )}
        </div>
      );
    }
    if (isDocx) {
      if (docx.error) {
        return (
          <div data-testid="review-error" role="alert" style={{ ...stateStyle, color: '#991b1b' }}>
            {docx.error}
          </div>
        );
      }
      if (docx.loading || docx.html === null) {
        return (
          <div data-testid="review-loading" style={stateStyle}>
            Loading preview…
          </div>
        );
      }
      return (
        <div style={viewerAreaStyle}>
          <iframe
            title="Document preview"
            data-testid="review-docx"
            sandbox=""
            srcDoc={docx.html}
            style={{
              flex: 1,
              width: '100%',
              minHeight: 320,
              border: '1px solid var(--border)',
              borderRadius: 4,
              background: '#fff',
            }}
          />
        </div>
      );
    }
    if (isXml) {
      // Synchronous identity gates: only read XML / SPL-parse state that was
      // produced FOR this document. A prior selection's content is suppressed
      // the instant the document changes, before the fetch effect re-runs.
      const xmlView = xml && xml.docId === documentId ? xml : null;
      const parseView = splParse.docId === documentId ? splParse : null;
      return (
        <div data-testid="review-xml" style={viewerAreaStyle}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4, gap: 8 }}>
            {xmlView?.isSpl && (
              <button
                data-testid="review-spl-parse-btn"
                type="button"
                disabled={parseView?.loading ?? false}
                onClick={() => {
                  const reqId = documentId as string;
                  setSplParse({ docId: reqId, loading: true, error: null, data: null });
                  v2.spl
                    .parseSpl(submissionId, reqId)
                    .then((metadata) =>
                      setSplParse((prev) =>
                        prev.docId === reqId
                          ? { ...prev, loading: false, data: metadata }
                          : prev,
                      ),
                    )
                    .catch((err: unknown) =>
                      setSplParse((prev) =>
                        prev.docId === reqId
                          ? {
                              ...prev,
                              loading: false,
                              error:
                                err instanceof Error ? err.message : 'Failed to parse SPL metadata',
                            }
                          : prev,
                      ),
                    );
                }}
                style={{ ...splParseBtn, opacity: parseView?.loading ? 0.6 : 1 }}
              >
                {parseView?.loading ? 'Parsing…' : 'Parse SPL Metadata'}
              </button>
            )}
          </div>
          {xmlView?.loading && (
            <div data-testid="review-loading" style={stateStyle}>
              Loading XML…
            </div>
          )}
          {xmlView?.error && (
            <div
              data-testid="review-error"
              role="alert"
              style={{
                padding: 12,
                background: '#FEE2E2',
                color: '#B91C1C',
                border: '1px solid #B91C1C',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              {xmlView.error}
            </div>
          )}
          {parseView?.error && (
            <div
              data-testid="review-spl-parse-error"
              role="alert"
              style={{
                padding: 8,
                background: '#FEF3C7',
                color: '#92400E',
                border: '1px solid #FCD34D',
                borderRadius: 4,
                fontSize: 12,
                marginBottom: 8,
              }}
            >
              {parseView.error}
            </div>
          )}
          {parseView?.data != null && (
            <div
              data-testid="review-spl-metadata"
              style={{
                marginBottom: 12,
                padding: 12,
                background: '#F0FDF4',
                border: '1px solid #BBF7D0',
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              <strong style={{ display: 'block', marginBottom: 6, color: '#15803D' }}>
                SPL Metadata Extracted
              </strong>
              <div><strong>Drug:</strong> {parseView.data.drugName || '—'}</div>
              <div><strong>Labeler Code:</strong> {parseView.data.labelerCode || '—'}</div>
              <div><strong>Application #:</strong> {parseView.data.applicationNumber ?? '—'}</div>
              <div><strong>Dosage Form:</strong> {parseView.data.dosageForm || '—'}</div>
              <div><strong>Route:</strong> {parseView.data.routeOfAdministration || '—'}</div>
              <div><strong>NDC Codes:</strong> {parseView.data.ndcCodes.join(', ') || '—'}</div>
            </div>
          )}
          {xmlView?.isSpl && xmlView.splHtml !== null && !xmlView.splHtmlLoading && (
            <div data-testid="review-spl" dangerouslySetInnerHTML={{ __html: xmlView.splHtml }} />
          )}
          {xmlView?.isSpl && xmlView.splHtmlLoading && (
            <div data-testid="review-loading" style={stateStyle}>
              Loading SPL preview…
            </div>
          )}
          {xmlView?.content != null &&
            (!xmlView.isSpl || (xmlView.splHtml === null && !xmlView.splHtmlLoading)) && (
              <div className="viewer-body">
                <pre
                  data-testid="review-xml-content"
                  dangerouslySetInnerHTML={{ __html: highlightXml(xmlView.content) }}
                  style={{ margin: 0, padding: 8, overflowX: 'auto', fontSize: 12, lineHeight: 1.5 }}
                />
              </div>
            )}
        </div>
      );
    }
    return (
      <div data-testid="review-non-pdf" style={stateStyle}>
        Inline review is available for PDF, .docx and XML files. This file is{' '}
        <code style={{ fontSize: 11 }}>{fileName ?? '(unknown type)'}</code>.
      </div>
    );
  }

  function renderCompare(): ReactElement {
    const selected = compareOptions.find((o) => o.id === eligibleCompareId) ?? null;
    return (
      <div data-testid="review-compare" style={compareLayout}>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 320 }}>
          <div style={compareLabel}>Current version</div>
          <PdfCanvasViewer
            idPrefix="review-compare-current"
            url={compareCurrent.url}
            loading={compareCurrent.loading}
            error={compareCurrent.error}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 320 }}>
          <div style={compareLabel}>Compare with</div>
          {compareOptions.length === 0 ? (
            <div data-testid="review-compare-no-siblings" style={stateStyle}>
              No other documents in this submission to compare against.
            </div>
          ) : (
            <>
              <select
                data-testid="review-compare-select"
                value={eligibleCompareId ?? ''}
                onChange={(e) => setCompareId(e.target.value)}
                style={compareSelect}
              >
                {compareOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.fileName}
                  </option>
                ))}
              </select>
              {selected ? (
                <PdfCanvasViewer
                  idPrefix="review-compare-target"
                  url={compareTarget.url}
                  loading={compareTarget.loading}
                  error={compareTarget.error}
                />
              ) : (
                <div style={stateStyle}>Pick a document to compare.</div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="workspace-review-panel"
      ref={containerRef}
      data-fullscreen={isFullscreen ? 'true' : 'false'}
      style={{ ...rootStyle, ...(isFullscreen ? fullscreenStyle : {}) }}
    >
      {sectionContextHeader}

      <div style={toolbarRow}>
        <PreviewNavCluster
          testIdPrefix="review"
          onPrevDocument={onPrevDocument}
          onNextDocument={onNextDocument}
          hasPrevDocument={hasPrevDocument}
          hasNextDocument={hasNextDocument}
          showPageControl={false}
        />
        {/* Contextual view selector — NOT a nested tablist (buttons + aria-pressed). */}
        <div role="group" aria-label="Review view" style={modeGroupStyle}>
          <button
            type="button"
            data-testid="review-mode-preview"
            aria-pressed={mode === 'preview'}
            onClick={() => setMode('preview')}
            style={modeBtnStyle(mode === 'preview')}
          >
            Preview
          </button>
          {isPdf && (
            <button
              type="button"
              data-testid="review-mode-compare"
              aria-pressed={mode === 'compare'}
              onClick={() => setMode('compare')}
              style={modeBtnStyle(mode === 'compare')}
            >
              Compare
            </button>
          )}
          <button
            type="button"
            data-testid="review-mode-annotations"
            aria-pressed={mode === 'annotations'}
            onClick={() => setMode('annotations')}
            style={modeBtnStyle(mode === 'annotations')}
          >
            Annotations
          </button>
        </div>
        <button
          type="button"
          data-testid="review-fullscreen-btn"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          style={{ ...toolbarBtnStyle, marginLeft: 'auto' }}
        >
          {isFullscreen ? 'Exit ⛶' : '⛶'}
        </button>
      </div>

      <div style={viewerAreaStyle}>
        {mode === 'annotations' ? (
          <div data-testid="review-annotations">
            <AnnotationPanel documentId={documentId} />
          </div>
        ) : mode === 'compare' ? (
          renderCompare()
        ) : (
          renderPreview()
        )}
      </div>
    </div>
  );
}

export default WorkspaceReviewPanel;
